import React, { useEffect, useState } from 'react';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeBox(box) {
  const x = Number(box?.x);
  const y = Number(box?.y);
  const width = Number(box?.width);
  const height = Number(box?.height);

  if (![x, y, width, height].every(Number.isFinite)) return null;
  if (width <= 0 || height <= 0) return null;

  return { x, y, width, height };
}

function luminance(r, g, b) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function estimateBackground(context, x0, y0, x1, y1, width, height) {
  const pad = 3;
  const sx0 = Math.max(0, x0 - pad);
  const sy0 = Math.max(0, y0 - pad);
  const sx1 = Math.min(width, x1 + pad);
  const sy1 = Math.min(height, y1 + pad);
  const image = context.getImageData(sx0, sy0, Math.max(1, sx1 - sx0), Math.max(1, sy1 - sy0));
  const values = [];

  const pushPixel = (offset) => {
    const r = image.data[offset];
    const g = image.data[offset + 1];
    const b = image.data[offset + 2];
    if (luminance(r, g, b) >= 185) values.push([r, g, b]);
  };

  // Sample the outer edge of the crop. This keeps the original paper/background colour
  // instead of replacing the whole bbox with an artificial white rectangle.
  for (let x = 0; x < image.width; x += Math.max(1, Math.floor(image.width / 12))) {
    pushPixel((0 * image.width + x) * 4);
    pushPixel(((image.height - 1) * image.width + x) * 4);
  }
  for (let y = 0; y < image.height; y += Math.max(1, Math.floor(image.height / 12))) {
    pushPixel((y * image.width) * 4);
    pushPixel((y * image.width + image.width - 1) * 4);
  }

  if (!values.length) return [255, 255, 255];
  values.sort((a, b) => luminance(...a) - luminance(...b));
  const mid = values[Math.floor(values.length / 2)];
  return mid;
}

function shouldRemoveInk(r, g, b, bg) {
  const distance = Math.hypot(r - bg[0], g - bg[1], b - bg[2]);
  const inkLuma = luminance(r, g, b);
  const bgLuma = luminance(...bg);
  // Remove only actual ink/anti-aliasing. Background pixels are preserved.
  return distance >= 10 || inkLuma < bgLuma - 5;
}

/*
 * Remove only the original glyph pixels inside the field/character boxes.
 * The source raster remains untouched everywhere else, so there are no visible
 * white rectangles around edited text.
 */
export default function EditedPageRaster({
  src,
  pageWidth,
  pageHeight,
  maskBoxes = [],
  className = '',
  style = {},
  alt = ''
}) {
  const [editedSrc, setEditedSrc] = useState(src || '');

  useEffect(() => {
    let cancelled = false;
    const boxes = Array.isArray(maskBoxes)
      ? maskBoxes.map(normalizeBox).filter(Boolean)
      : [];

    if (!src || !boxes.length || pageWidth <= 0 || pageHeight <= 0) {
      setEditedSrc(src || '');
      return undefined;
    }

    const image = new Image();
    image.decoding = 'async';

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, image.naturalWidth || image.width);
        canvas.height = Math.max(1, image.naturalHeight || image.height);

        const context = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
        if (!context) {
          if (!cancelled) setEditedSrc(src);
          return;
        }

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const scaleX = canvas.width / Number(pageWidth);
        const scaleY = canvas.height / Number(pageHeight);

        for (const box of boxes) {
          const padX = Math.max(1, Math.round(scaleX * 0.35));
          const padY = Math.max(1, Math.round(scaleY * 0.35));
          const x0 = clamp(Math.floor(box.x * scaleX) - padX, 0, canvas.width - 1);
          const y0 = clamp(Math.floor(box.y * scaleY) - padY, 0, canvas.height - 1);
          const x1 = clamp(Math.ceil((box.x + box.width) * scaleX) + padX, 1, canvas.width);
          const y1 = clamp(Math.ceil((box.y + box.height) * scaleY) + padY, 1, canvas.height);
          if (x1 <= x0 || y1 <= y0) continue;

          const background = estimateBackground(context, x0, y0, x1, y1, canvas.width, canvas.height);
          const pixels = context.getImageData(x0, y0, x1 - x0, y1 - y0);
          const data = pixels.data;

          for (let yy = 0; yy < pixels.height; yy += 1) {
            for (let xx = 0; xx < pixels.width; xx += 1) {
              const index = (yy * pixels.width + xx) * 4;
              const r = data[index];
              const g = data[index + 1];
              const b = data[index + 2];
              if (!shouldRemoveInk(r, g, b, background)) continue;

              data[index] = background[0];
              data[index + 1] = background[1];
              data[index + 2] = background[2];
              data[index + 3] = 255;
            }
          }

          context.putImageData(pixels, x0, y0);
        }

        const next = canvas.toDataURL('image/png');
        if (!cancelled) setEditedSrc(next);
      } catch {
        if (!cancelled) setEditedSrc(src);
      }
    };

    image.onerror = () => {
      if (!cancelled) setEditedSrc(src);
    };

    image.src = src;
    return () => { cancelled = true; };
  }, [src, pageWidth, pageHeight, JSON.stringify(maskBoxes)]);

  return (
    <img
      className={className}
      src={editedSrc || src}
      alt={alt}
      draggable={false}
      style={style}
    />
  );
}
